import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  ArchiveIcon,
  BellIcon,
  BriefcaseIcon,
  CalendarIcon,
  ChartBarIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardCheckIcon,
  ClipboardListIcon,
  CogIcon,
  CollectionIcon,
  CubeIcon,
  CubeTransparentIcon,
  CurrencyRupeeIcon,
  DocumentReportIcon,
  DocumentTextIcon,
  FolderOpenIcon,
  IdentificationIcon,
  InboxInIcon,
  LibraryIcon,
  LocationMarkerIcon,
  MapIcon,
  MenuIcon,
  OfficeBuildingIcon,
  PresentationChartLineIcon,
  ReceiptTaxIcon,
  SearchIcon,
  ShieldCheckIcon,
  ShoppingCartIcon,
  SupportIcon,
  TemplateIcon,
  TruckIcon,
  UserGroupIcon,
  UsersIcon,
  ViewGridIcon,
} from "@heroicons/react/outline";
import { useAuth } from "../../context/AuthContext";
import { useSidebar } from "../../context/SidebarContext";
import { canAccessAction } from "../../routes/routePermissions";

interface SidebarLink {
  path: string;
  label: string;
  icon: ReactNode;
  action: string;
}

interface SidebarSection {
  category: string;
  icon: ReactNode;
  links: SidebarLink[];
}

type HeroIcon = React.ComponentType<React.SVGProps<SVGSVGElement>>;

const sectionIcon = (Icon: HeroIcon) => <Icon aria-hidden="true" className="h-5 w-5 stroke-[2]" />;
const linkIcon = (Icon: HeroIcon) => <Icon aria-hidden="true" className="h-5 w-5 stroke-[2]" />;
const controlIcon = "h-4 w-4 stroke-[2]";

const sidebarSections: SidebarSection[] = [
  {
    category: "Dashboard",
    icon: sectionIcon(ViewGridIcon),
    links: [{ path: "/admin/dashboard", label: "Dashboard", icon: linkIcon(ViewGridIcon), action: "view_dashboard" }],
  },
  {
    category: "Sales",
    icon: sectionIcon(PresentationChartLineIcon),
    links: [
      { path: "/admin/enquiries", label: "Enquiries", icon: linkIcon(InboxInIcon), action: "view_enquiries" },
      { path: "/admin/quotations", label: "Quotations", icon: linkIcon(DocumentTextIcon), action: "view_quotations" },
      { path: "/admin/clients", label: "Clients", icon: linkIcon(UserGroupIcon), action: "manage_clients" },
      { path: "/admin/sales-customers", label: "Sales Customers", icon: linkIcon(UsersIcon), action: "manage_sales_customers" },
    ],
  },
  {
    category: "Projects",
    icon: sectionIcon(OfficeBuildingIcon),
    links: [
      { path: "/admin/projects", label: "Projects", icon: linkIcon(OfficeBuildingIcon), action: "view_projects" },
      { path: "/admin/project-documents", label: "Project Documents", icon: linkIcon(FolderOpenIcon), action: "manage_project_documents" },
      { path: "/admin/subcontract-works", label: "Subcontract Works", icon: linkIcon(BriefcaseIcon), action: "manage_subcontract_works" },
      { path: "/admin/daily-tasks", label: "Daily Tasks", icon: linkIcon(ClipboardCheckIcon), action: "track_daily_tasks" },
      { path: "/admin/daily-notes", label: "Daily Notes", icon: linkIcon(ClipboardListIcon), action: "write_daily_notes" },
    ],
  },
  {
    category: "Finance",
    icon: sectionIcon(CurrencyRupeeIcon),
    links: [
      { path: "/admin/accounts", label: "Accounts", icon: linkIcon(LibraryIcon), action: "manage_accounts" },
      { path: "/admin/expenses", label: "Expenses", icon: linkIcon(ReceiptTaxIcon), action: "log_expenses" },
      { path: "/admin/security-deposits", label: "Security Deposits", icon: linkIcon(ShieldCheckIcon), action: "manage_security_deposits" },
      { path: "/admin/EMD", label: "EMD", icon: linkIcon(CurrencyRupeeIcon), action: "manage_EMD" },
      { path: "/admin/travel-allowance", label: "Travel Allowance", icon: linkIcon(LocationMarkerIcon), action: "approve_travel_allowance" },
    ],
  },
  {
    category: "Operations",
    icon: sectionIcon(CubeTransparentIcon),
    links: [
      { path: "/admin/assets", label: "Assets", icon: linkIcon(ArchiveIcon), action: "manage_assets" },
      { path: "/admin/purchases", label: "Purchases", icon: linkIcon(ShoppingCartIcon), action: "view_purchase_requests" },
      { path: "/admin/stock-management", label: "Stock Management", icon: linkIcon(CubeTransparentIcon), action: "manage_stock" },
      { path: "/admin/labour-management", label: "Labour Management", icon: linkIcon(UserGroupIcon), action: "manage_labour" },
      { path: "/admin/vehicle-management", label: "Vehicle Management", icon: linkIcon(TruckIcon), action: "manage_vehicle" },
      { path: "/admin/tools-management", label: "Tools Management", icon: linkIcon(CogIcon), action: "manage_tools" },
      { path: "/admin/hr", label: "HR", icon: linkIcon(IdentificationIcon), action: "view_hr" },
      { path: "/admin/employees", label: "Employees", icon: linkIcon(UserGroupIcon), action: "manage_employees" },
      { path: "/admin/users", label: "Users", icon: linkIcon(UsersIcon), action: "manage_users" },
    ],
  },
  {
    category: "Masters",
    icon: sectionIcon(CollectionIcon),
    links: [
      { path: "/admin/services", label: "Services", icon: linkIcon(SupportIcon), action: "define_services" },
      { path: "/admin/suppliers", label: "Suppliers", icon: linkIcon(TruckIcon), action: "manage_suppliers" },
      { path: "/admin/materials", label: "Materials", icon: linkIcon(CubeIcon), action: "manage_materials" },
      { path: "/admin/bank-details", label: "Bank Details", icon: linkIcon(LibraryIcon), action: "manage_bank_accounts" },
      { path: "/admin/work-types", label: "Work Types", icon: linkIcon(CogIcon), action: "define_work_types" },
      { path: "/admin/work-categories", label: "Work Categories", icon: linkIcon(CollectionIcon), action: "define_work_categories" },
      { path: "/admin/project-sections", label: "Project Sections", icon: linkIcon(TemplateIcon), action: "define_project_sections" },
      { path: "/admin/work-locations", label: "Work Locations", icon: linkIcon(MapIcon), action: "define_work_locations" },
      { path: "/admin/tax-types", label: "Tax Types", icon: linkIcon(ReceiptTaxIcon), action: "define_tax_types" },
      { path: "/admin/holidays", label: "Holidays", icon: linkIcon(CalendarIcon), action: "define_holidays" },
    ],
  },
  {
    category: "Reports",
    icon: sectionIcon(ChartBarIcon),
    links: [
      { path: "/admin/reports", label: "Reports", icon: linkIcon(ChartBarIcon), action: "view_reports" },
      { path: "/admin/attendance-report", label: "Attendance Report", icon: linkIcon(ClipboardCheckIcon), action: "view_attendance_reports" },
      { path: "/admin/activity-logs", label: "Activity Logs", icon: linkIcon(DocumentReportIcon), action: "view_activity_logs" },
    ],
  },
  {
    category: "Others",
    icon: sectionIcon(BellIcon),
    links: [
      { path: "/admin/reminders", label: "Reminders", icon: linkIcon(BellIcon), action: "set_reminders" },
      { path: "/admin/settings", label: "Settings", icon: linkIcon(CogIcon), action: "configure_settings" },
    ],
  },
];

const isPathActive = (currentPath: string, linkPath: string) =>
  currentPath === linkPath || currentPath.startsWith(`${linkPath}/`);

const getActiveSection = (pathname: string, sections: SidebarSection[] = sidebarSections) =>
  sections.find((section) =>
    section.links.some((link) => isPathActive(pathname, link.path))
  )?.category || "Dashboard";

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { permissions, roles } = useAuth();
  const { isCollapsed, toggleSidebar, closeSidebar } = useSidebar();
  const [searchTerm, setSearchTerm] = useState("");
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() =>
    sidebarSections.reduce<Record<string, boolean>>((acc, section) => {
      acc[section.category] = ["Dashboard", "Sales", "Projects"].includes(section.category);
      return acc;
    }, {})
  );

  const accessibleSections = useMemo(
    () =>
      sidebarSections
        .map((section) => ({
          ...section,
          links: section.links.filter((link) => canAccessAction(link.action, permissions, roles)),
        }))
        .filter((section) => section.links.length > 0),
    [permissions, roles]
  );

  useEffect(() => {
    const activeCategory = getActiveSection(location.pathname, accessibleSections);
    setOpenSections((current) => ({ ...current, [activeCategory]: true }));
  }, [accessibleSections, location.pathname]);

  const filteredSections = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) {
      return accessibleSections;
    }

    return accessibleSections
      .map((section) => ({
        ...section,
        links: section.links.filter((link) => link.label.toLowerCase().includes(term)),
      }))
      .filter((section) => section.links.length > 0);
  }, [accessibleSections, searchTerm]);

  const handleCollapsedNavigate = (section: SidebarSection) => {
    const activeLink = section.links.find((link) => isPathActive(location.pathname, link.path));
    navigate(activeLink?.path || section.links[0].path);
  };

  const handleLinkClick = () => {
    if (window.innerWidth < 1024) {
      closeSidebar();
    }
  };

  return (
    <>
      {!isCollapsed && (
        <button
          type="button"
          aria-label="Close navigation"
          className="fixed inset-0 z-30 bg-slate-900/25 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-40 flex border-r border-slate-200 bg-[#f4f5f7] text-slate-700 shadow-none transition-all duration-200 ${
          isCollapsed ? "w-16 -translate-x-full lg:translate-x-0" : "w-[248px] translate-x-0"
        }`}
      >
        {isCollapsed ? (
          <div className="hidden h-full w-16 flex-col items-center gap-2 px-2 py-3 lg:flex">
            <button
              type="button"
              onClick={toggleSidebar}
              className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg text-slate-500 transition hover:bg-white hover:text-slate-900"
              aria-label="Expand navigation"
              title="Expand navigation"
            >
              <MenuIcon aria-hidden="true" className={controlIcon} />
            </button>

            {accessibleSections.map((section) => {
              const active = section.links.some((link) => isPathActive(location.pathname, link.path));
              return (
                <button
                  key={section.category}
                  type="button"
                  onClick={() => handleCollapsedNavigate(section)}
                  className={`group relative flex h-10 w-10 items-center justify-center rounded-lg transition ${
                    active ? "bg-white text-slate-950 shadow-sm" : "text-slate-500 hover:bg-white hover:text-slate-900"
                  }`}
                  title={section.category}
                >
                  {section.icon}
                  <span className="cms-type-badge pointer-events-none absolute left-full ml-2 rounded-md bg-slate-950 px-2 py-1 text-white opacity-0 shadow-sm transition group-hover:opacity-100">
                    {section.category}
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="flex h-full w-full flex-col">
            <div className="flex items-center justify-between px-4 pb-2 pt-3">
              <button
                type="button"
                onClick={() => navigate("/admin/dashboard")}
                className="flex min-w-0 items-center gap-2 rounded-lg text-left"
              >
                <span className="cms-type-badge flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-slate-900 shadow-sm">
                  CMS
                </span>
                <span className="cms-type-sidebar-item truncate font-bold text-slate-900">Construction</span>
              </button>
              <button
                type="button"
                onClick={toggleSidebar}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition hover:bg-white hover:text-slate-900"
                aria-label="Collapse navigation"
                title="Collapse navigation"
              >
                <ChevronLeftIcon aria-hidden="true" className="h-3.5 w-3.5 stroke-[2.2]" />
              </button>
            </div>

            <div className="px-3 pb-3">
              <div className="relative">
                <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 stroke-[2.2] text-slate-400" />
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search"
                  className="cms-type-form-control h-9 w-full rounded-md border border-transparent bg-white/75 pl-8 pr-3 outline-none transition placeholder:text-slate-400 focus:border-slate-300 focus:bg-white focus:ring-2 focus:ring-slate-200"
                />
              </div>
            </div>

            <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto px-2 pb-3">
              {filteredSections.map((section) => {
                const hasActiveLink = section.links.some((link) => isPathActive(location.pathname, link.path));
                const isOpen = searchTerm.trim() ? true : openSections[section.category];
                const sectionPanelId = `sidebar-section-${section.category.toLowerCase().replace(/\s+/g, "-")}`;

                return (
                  <div key={section.category}>
                    <button
                      type="button"
                      onClick={() =>
                        setOpenSections((current) => ({
                          ...current,
                          [section.category]: !current[section.category],
                        }))
                      }
                      aria-expanded={isOpen}
                      aria-controls={sectionPanelId}
                      className={`cms-type-sidebar-section flex items-center w-full text-left p-2 md:p-3 rounded-lg transition-all duration-200 ${
                        hasActiveLink ? "text-slate-900" : "text-slate-500 hover:bg-white/60 hover:text-slate-800"
                      }`}
                    >
                      <span className={`mr-2 md:mr-3 flex-shrink-0 ${hasActiveLink ? "text-slate-700" : "text-slate-500"}`}>
                        {section.icon}
                      </span>
                      <span className="text-xs md:text-sm font-medium truncate flex-1">{section.category}</span>
                      {isOpen ? (
                        <ChevronDownIcon aria-hidden="true" className="h-3.5 w-3.5 stroke-[2.2] flex-shrink-0" />
                      ) : (
                        <ChevronRightIcon aria-hidden="true" className="h-3.5 w-3.5 stroke-[2.2] flex-shrink-0" />
                      )}
                    </button>

                    {isOpen && (
                      <ul id={sectionPanelId} className="mt-1 space-y-1 pl-4">
                        {section.links.map((link) => {
                          const active = isPathActive(location.pathname, link.path);
                          return (
                            <li key={link.path}>
                              <NavLink
                                to={link.path}
                                onClick={handleLinkClick}
                                className={`cms-type-sidebar-item flex items-center w-full text-left p-2 md:p-3 rounded-lg transition-all duration-200 ${
                                  active
                                    ? "bg-white text-slate-950 shadow-sm"
                                    : "text-slate-500 hover:bg-white/70 hover:text-slate-900"
                                }`}
                              >
                                <span className={`mr-2 md:mr-3 flex-shrink-0 ${active ? "text-slate-700" : "text-slate-400"}`}>
                                  {link.icon}
                                </span>
                                <span className="text-xs md:text-sm font-medium truncate">{link.label}</span>
                              </NavLink>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                );
              })}

              {filteredSections.length === 0 && (
                <div className="cms-type-empty-state px-3 py-8 text-center text-slate-400">No navigation matches.</div>
              )}
            </nav>
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;
